import { motion, useScroll, useSpring } from 'framer-motion';

interface ScrollIndicatorProps {
  modules: Array<{
    src: string;
    alt: string;
    type: 'single' | 'twoColumn' | 'video' | 'carousel' | 'text';
    pairIndex?: number;
    videoId?: string;
    videoType?: 'mp4' | 'youtube';
    images?: Array<{ src: string; alt: string; }>; // For carousel modules
    text?: string; // For text modules
  }>;
  containerRef: React.RefObject<HTMLElement>;
  variant?: 'light' | 'dark';
  activeIndices?: number[];
}

export const ScrollIndicator: React.FC<ScrollIndicatorProps> = ({ modules, containerRef, activeIndices = [] }) => {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <>
      {/* Desktop: Thumbnails and Progress Bar Group */}
      <div className="fixed top-0 left-[-10px] h-screen w-[100px] z-[9999] hidden lg:block">
        {/* Vertical progress bar */}
        <div className="absolute left-[40px] top-0 h-full w-[1px] bg-gray-200 dark:bg-gray-700">
          <motion.div 
            className="w-full h-full bg-[#0048FF] origin-top"
            style={{ 
              scaleY: smoothProgress,
              transformOrigin: 'top'
            }}
          />
        </div>

        {/* Thumbnails */}
        <div className="absolute top-1/2 left-[50px] -translate-y-1/2 flex flex-col items-start gap-2">
          {modules.map((module, index) => {
            const isActive = activeIndices.includes(index);
            return (
              <motion.div
                key={index}
                className={`w-[40px] aspect-[4/3] overflow-hidden transition-all duration-300 relative ${
                  isActive ? 'opacity-100' : 'opacity-20'
                }`}
                style={{ marginLeft: 'auto' }}
                whileHover={{ scale: 1.05 }}
              >
                {module.type === 'video' ? (
                  // Video thumbnail with play icon
                  <div className="w-full h-full bg-gray-400 dark:bg-gray-600 flex items-center justify-center">
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="white" 
                      className="ml-1"
                    >
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                ) : module.type === 'carousel' && module.images && module.images.length > 0 ? (
                  // Carousel thumbnail - show first image
                  <img
                    src={module.images[0].src}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : module.type === 'text' ? (
                  // Text thumbnail with text icon
                  <div className="w-full h-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center">
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="currentColor" 
                      className="text-gray-600 dark:text-gray-400"
                    >
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                  </div>
                ) : (
                  // Regular image thumbnail
                  <img
                    src={module.src}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
                {isActive && (
                  <div className="absolute inset-0 bg-[#0048FF]" style={{ opacity: 1 }} />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Mobile: Full Height Progress Bar */}
      <div className="fixed top-0 bottom-0 left-0 w-[4px] z-[9999] lg:hidden">
        {/* Background bar */}
        <div className="w-full h-full bg-gray-200 dark:bg-gray-700">
          <motion.div 
            className="w-full h-full bg-[#0048FF] origin-top"
            style={{ 
              scaleY: smoothProgress,
              transformOrigin: 'top'
            }}
          />
        </div>
      </div>
    </>
  );
}; 